/* THE SIZE OF THE PICTURE, DEFINED ONCE.
 *
 * `game.js` used to own these, with a comment explaining that `scale.js` had
 * once declared its own `VW = 256, VH = 240` while `main.js` imported VW from
 * `game.js` and `pickScale` from `scale.js` — so changing one would have sized
 * the canvas for one width and scaled it for another, with nothing comparing
 * them.
 *
 * That argument is why this file exists rather than why `hud.js` should copy
 * the line. `hud.js` needs the same three numbers, and importing them back out
 * of `game.js` would make the two files a cycle, so the definition moves down
 * to where both can reach it. `game.js` re-exports all three, because
 * `main.js` and the playground import them from there.
 */
import { VW as SCREEN_W, VH as SCREEN_H } from './scale.ts';
import { PH } from './entities.ts';
import { HOTBAR, BAG_SLOTS } from './sim/inventory.ts';

export const VW = SCREEN_W;
export const VH = SCREEN_H;
export const HUD_H = SCREEN_H - PH;

/** Roman numerals for the three hollows, used by the HUD and by `say()`. */
export const ROMAN = ['I', 'II', 'III', 'IV'];

/** How many vigor pips fit on one HUD row before it wraps to a second. */
export const VIGOR_PER_ROW = 11;

/** A frame count as m:ss, for the run clock. */
export const clock = (frames) => {
  const total = Math.max(0, Math.round(frames / 60));
  return Math.floor(total / 60) + ':' + String(total % 60).padStart(2, '0');
};

/** The bag screen's geometry (LS-229): the grid in the lower half, where a thumb lives, and the two boxes under it. Read by hud.ts (drawing) and game.ts (taps). */
export const HOTBAR_X = 60, HOTBAR_Y = 4, HOTBAR_PITCH = 15;   // in the 24-row strip (2026-09-27)
/* THE PACK SITS WHERE THE EYE IS, not at the foot of the picture. At `HUD_H + 96` there were sixty rows of dimmed
 * world between the strip that names the screen and the panel itself, and the worn column ran out of room at the
 * bottom -- every plate too short for the name cut under it. Lifting the whole panel pays for both. */
export const BAG_X0 = Math.round((VW - 9 * 15) / 2), BAG_Y0 = HUD_H + 66, BAG_BOX_Y = VH - 32;
/** THE LOADOUT'S BOXES (LS-239): hand (the picked weapon, shown), body, feet, charm. A column to the right of the grid
 *  on a desk; a row under the title on a phone, where the grid takes the width. */
export function loadoutBoxes(touch: boolean): Array<['hand' | 'body' | 'feet' | 'charm', number, number]> {
  if (touch) return [['hand', 22, HUD_H + 36], ['body', 80, HUD_H + 36], ['feet', 138, HUD_H + 36], ['charm', 196, HUD_H + 36]];
  // twenty-four apart, not seventeen: each worn box carries its own name cut under it, and the name needs a row (2026-09-05)
  /* PITCH 34, BECAUSE THE PLATES ARE 32 TALL. At a pitch of 24 under 30-tall plates every worn plate overlapped the
   * one below it by six rows and its name -- BODY, FEET, CHARM -- was cut across the seam between two plates; at 27
   * tall there was one row between the well's foot and the plate's, so the name landed on the bottom bevel. */
  return [['hand', 226, HUD_H + 4], ['body', 226, BAG_Y0 - 5], ['feet', 226, BAG_Y0 + 29], ['charm', 226, BAG_Y0 + 63]];   // the hand box sits in the title strip, clear of the cursor's caption
}
/** Paging changes presentation only; cursor and saved slot indices remain global. */
export function bagPitch(touch: boolean, scale = 1.25, cursor = 0) {
  if (!touch) return { px: 15, py: 17, x0: BAG_X0, y0: BAG_Y0, cols: HOTBAR, rows: 3, offset: 0, count: BAG_SLOTS, page: 0, pages: 1 };
  const unit = Math.max(36, Math.ceil(44 / Math.max(0.25, scale)));
  const cols = Math.max(1, Math.floor((VW - 16) / unit));
  const px = Math.floor((VW - 16) / cols), py = unit, y0 = HUD_H + 76;
  const rows = Math.max(1, Math.floor((bagButtons(true, scale)[0].y - 4 - y0) / py));
  const count = cols * rows, pages = Math.ceil(BAG_SLOTS / count);
  const page = Math.min(pages - 1, Math.floor(cursor / count));
  return { px, py, x0: Math.round((VW - cols * px) / 2), y0, cols, rows, offset: page * count, count, page, pages };
}

export function bagButtons(touch: boolean, scale = 1.25) {
  const height = Math.max(36, Math.ceil(44 / Math.max(0.25, scale)));
  return touch
    ? ['previous', 'drop', 'close', 'next'].map((action, i) => ({ action, x: 4 + i * 63, y: VH - height - 4, w: 59, h: height }))
    : [ ['drop', 40], ['close', 148] ].map(([action, x]) => ({ action: String(action), x: Number(x), y: BAG_BOX_Y, w: 68, h: 26 }));
}
